// Marks the tooltips set here, so tooltips of the components themselves are never replaced
const OWN_TITLE = 'data-full-text-title';

// Space taken by the arrow of a select besides its padding
const SELECT_ARROW_WIDTH = 20;

let canvasContext: CanvasRenderingContext2D | null = null;

const getTextWidth = (element: Element, text: string): number => {
  if (canvasContext === null) {
    canvasContext = document.createElement('canvas').getContext('2d');
  }

  if (canvasContext === null) {
    return 0;
  }

  const style = window.getComputedStyle(element);
  canvasContext.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
  return canvasContext.measureText(text).width;
};

const getContentWidth = (element: Element): number => {
  const style = window.getComputedStyle(element);
  return (
    element.clientWidth -
    parseFloat(style.paddingLeft) -
    parseFloat(style.paddingRight)
  );
};

/** The text that is cut off in the element, or null when it fits */
export const getHiddenText = (element: Element): string | null => {
  if (element instanceof HTMLSelectElement) {
    const text = element.selectedOptions[0]?.text ?? '';
    return getTextWidth(element, text) >
      getContentWidth(element) - SELECT_ARROW_WIDTH
      ? text
      : null;
  }

  if (element instanceof HTMLInputElement) {
    if (!['text', 'search', ''].includes(element.type)) {
      return null;
    }

    const text = element.value;
    return element.scrollWidth > element.clientWidth ||
      getTextWidth(element, text) > getContentWidth(element) + 1
      ? text
      : null;
  }

  if (element instanceof HTMLElement && element.children.length === 0) {
    const text = element.innerText.trim();
    return text !== '' && element.scrollWidth > element.clientWidth
      ? text
      : null;
  }

  return null;
};

/** The first cut-off text inside the element (e.g. the selected value of a dropdown) */
const getHiddenTextInside = (element: HTMLElement): string | null => {
  const texts = Array.from(element.querySelectorAll('div, span')).filter(
    (child) => child.children.length === 0
  );

  return texts.reduce<string | null>(
    (found, child) => found ?? getHiddenText(child),
    null
  );
};

/**
 * Mouse over handler for a container: when the pointer is on a field or text whose
 * content does not fit in it, the whole text is shown as a tooltip.
 */
const showFullTextOnHover = (event: { target: EventTarget | null }): void => {
  if (!(event.target instanceof HTMLElement)) {
    return;
  }

  // In a search dropdown the selected text is covered by the search input, so the
  // tooltip is set on the whole dropdown
  const searchField = event.target.closest('.search-field');
  const target =
    searchField instanceof HTMLElement ? searchField : event.target;

  if (target.hasAttribute('title') && !target.hasAttribute(OWN_TITLE)) {
    return;
  }

  const hiddenText =
    target === searchField
      ? getHiddenTextInside(target)
      : getHiddenText(target);
  if (hiddenText === null) {
    if (target.hasAttribute(OWN_TITLE)) {
      target.removeAttribute('title');
      target.removeAttribute(OWN_TITLE);
    }
    return;
  }

  target.setAttribute('title', hiddenText);
  target.setAttribute(OWN_TITLE, '');
};

export default showFullTextOnHover;
